(ns nosco.views.channel-create-modal
  "The channel creation dialog, see Figma \"Channel Creation\".

  Step 1 is choosing where to start from: a blank channel, an import, a
  template or an existing channel. Step 2 asks for the details, depending on
  that choice. Creating lands on the new (draft) channel's general settings."
  (:require
   [clojure.edn :as edn]
   [clojure.string :as string]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.controllers :as controllers]
   [nosco.graphql-client :as gql-client]
   [nosco.graphql.errors :as gql-errors]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.router :as router]
   [nosco.tracking.track :as track]
   [nosco.ui.banners :as banners]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.channel-cards :as cards]
   [nosco.ui.checkbox :as checkbox]
   [nosco.ui.chips :as chips]
   [nosco.ui.combobox-multi2 :as combobox-multi]
   [nosco.ui.dropzone :as dropzone]
   [nosco.ui.errors :as ui-errors]
   [nosco.ui.html :as html]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.loading :as loading]
   [nosco.ui.modals3 :as modals3]
   [nosco.ui.progress :as progress]
   [nosco.ui.select3 :as select]
   [nosco.ui.tabs2 :as tabs2]
   [nosco.ui.text :as text]
   [nosco.util :as util]
   [nosco.util.format :as format]
   [react :as React]
   [reseda.react :as rr]
   [reseda.state :as rs])
  (:require-macros [nosco.conf.locales :as locales]))

(def search-spaces-query (gql-client/graphql-query "./IdeaboxCreate.graphql" :SearchSpaces))
(def create-channel-mutation (gql-client/graphql-query "./IdeaboxCreate.graphql" :CreateChannel))
(def templates-list-query (gql-client/graphql-query "./IdeaboxesList.graphql" :IdeaboxTemplatesList))
(def create-from-template-mutation (gql-client/graphql-query "./IdeaboxCreateFromTemplate.graphql"))
(def copy-channel-mutation (gql-client/graphql-query "./IdeaboxCopy.graphql"))
(def import-channel-mutation (gql-client/graphql-query "./IdeaboxImportChannel.graphql"))
(def search-channels-query (gql-client/graphql-query "./ChannelCreate.graphql" :SearchChannelsToDuplicate))
(def search-users-query (gql-client/graphql-query "./ChannelCreate.graphql" :SearchUsersForChannelAdmins))
(def add-admins-mutation (gql-client/graphql-query "./ChannelCreate.graphql" :AddChannelAdmins))

;; ---------------------------------------------------------------------------
;; Helpers
;; ---------------------------------------------------------------------------

(defonce ^:private dialog-state (atom {:open? false}))
(defonce ^:private dialog-store (rs/new-store dialog-state))

(defn- hide! [mutate-history?]
  (router/unwind-transient-url-stack! ::dialog mutate-history?)
  (swap! dialog-state assoc :open? false))

(defn- show! []
  (router/initiate-transient-url-context! ::dialog (fn [_] (hide! false)) nil)
  (router/push-transient-url! ::dialog (router/href :nosco.routes/ideaboxes-copy))
  ;; the router doesn't see a transient URL, so it doesn't send the route's page view
  (track/page-view "Copy Channel Template")
  (swap! dialog-state assoc :open? true))

(defn- show-dialog-handler
  "Opens the dialog over the current page when a link to the
  :nosco.routes/ideaboxes-copy page is clicked."
  [match ^js e _el _uri]
  (when (= :nosco.routes/ideaboxes-copy (get-in match [:data :name]))
    (.preventDefault e)
    (.stopPropagation e)
    (show!)
    true))

(def blank-template-id "blank-channel")

(def channels-page-size 24)

(defn- go-to-settings!
  "Replaces the dialog's history entry, so Back from the new channel's settings
  doesn't open the dialog again."
  [ideabox-id]
  (when (:open? @dialog-state)
    (hide! false))
  (router/replace-state :nosco.routes/ideaboxes-single-settings-general {:ideabox-id ideabox-id}))

(defn- add-admins!
  "Makes `admins` admins of the new channel."
  [ideabox-id admins]
  (if (empty? admins)
    (js/Promise.resolve nil)
    (gql-client/mutation-promise! add-admins-mutation
                                  {:ideaboxId ideabox-id
                                   :selection {:only_ids (mapv :id admins)}})))

(defn- finish!
  "Adds the admins, then lands on the new channel's general settings."
  [ideabox-id admins]
  (-> (add-admins! ideabox-id admins)
      (.finally #(go-to-settings! ideabox-id))))

(defn- first-paragraph-text
  "The text of the first paragraph of an HTML description."
  [html]
  (when-not (string/blank? html)
    (let [body (html/parse-html html)]
      (string/trim (or (some-> (.querySelector body "p") .-textContent)
                       (.-textContent body))))))

(defn export-title
  "The original channel title stored in an export EDN string, or nil if it can't
  be read. The package carries nosco-specific tagged literals (#oid,
  #nosco/export-ref, ...) we don't care about here, so unknown tags are passed
  through untouched — we only reach into [:channel :title], a plain string."
  [edn-string]
  (try
    (-> (edn/read-string {:default (fn [_tag value] value)} edn-string)
        (get-in [:channel :title]))
    (catch :default _ nil)))

(defn- use-submit
  "Returns [loading submit!]. `create!` returns a promise of the id of the new
  channel, or of nothing when creating failed (the error is already shown)."
  [create!]
  (let [[loading set-loading] (hooks/useState false)]
    [loading
     (fn []
       (set-loading true)
       (-> (create!)
           (.then (fn [ideabox-id]
                    (when-not ideabox-id
                      (set-loading false))))
           (.catch (fn [_err]
                     (set-loading false)))))]))

;; Presentation of the templates. The templates come from the server with just
;; a title and a description, so the icon, accent colour and category the
;; design shows for them are kept here, keyed by the template ids in
;; resources/channel_templates/*.edn.
(def template-presentation
  {"innovation-challenge" {:icon icons2/Award-large :color :magenta :category :ideation}
   "idea-campaign" {:icon icons2/Flag-large :color :magenta :category :ideation}
   "open-idea-box" {:icon icons2/Lightbulb-large :color :magenta :category :ideation}
   "continuous-improvement" {:icon icons2/Timeline-large :color :lime :category :pipelines}
   "project-pipeline" {:icon icons2/Steppers-large :color :lime :category :pipelines}
   "innovation-project-pipeline" {:icon icons2/Kanban-large :color :lime :category :pipelines}
   "innovation-project-pipeline-npv-roi" {:icon icons2/ChartLine-large :color :lime :category :pipelines}
   "ai-opportunity-pipeline" {:icon icons2/AI-large :color :lime :category :pipelines}
   "startup-scouting-pipeline" {:icon icons2/ViewShow-large :color :lime :category :pipelines}
   "trend-radar" {:icon icons2/Target-large :color :teal :category :insights}
   "insights-observations" {:icon icons2/Note-large :color :teal :category :insights}})

;; templates the server knows but we don't have a presentation for (yet)
(def default-template-presentation
  {:icon icons2/Channels-large :color :neutral :category nil})

(defn- presentation [template-id]
  (get template-presentation template-id default-template-presentation))

(defn- categories []
  [{:key :insights :title (t/t "Insights") :color "#6cc3e0"}
   {:key :ideation :title (t/t "Ideation") :color "#f797d2"}
   {:key :pipelines :title (t/t "Pipelines") :color "#b3df72"}])

;; ---------------------------------------------------------------------------
;; Shared fields
;; ---------------------------------------------------------------------------

(defn- fetch-spaces* []
  (-> (gql-client/query-promise! search-spaces-query {})
      (.then (fn [r] (-> r :data :searchSpaces :nodes)))
      (ui-errors/suspending-value)))

(defnc SpacesSelect* [{:keys [spaces* space-id set-space-id]}]
  [select/SelectSimple {:on-change (fn [{:keys [value]}]
                                     (set-space-id value))
                        :placeholder (t/t "Select")
                        :value space-id
                        :options (mapv (fn [{:keys [id title]}]
                                         {:value id
                                          :label title})
                                       (rr/deref* spaces*))}])

(defnc SpacesSelect [{:keys [space-id set-space-id]}]
  ;; the Suspense boundary must stay below the useState holding the fetch: one
  ;; above it would remount this on suspend, and refetch
  (let [[spaces*] (hooks/useState fetch-spaces*)]
    [inputs/FormField {:label (t/t "Space")
                       :description (t/t "Group and organise channels by e.g. business unit or use case")}
     [React/Suspense {:fallback (hx/f [select/SelectSimple {:placeholder (t/t "Select")
                                                            :value space-id
                                                            :disabled true
                                                            :options []}])}
      [SpacesSelect* {:spaces* spaces*
                      :space-id space-id
                      :set-space-id set-space-id}]]]))

(defn- search-users! [{:keys [search-string results-size filters]}]
  (-> (gql-client/query-promise! search-users-query
                                 (cond-> {}
                                   search-string (assoc :name search-string)
                                   results-size (assoc :pagination {:first results-size})
                                   filters (assoc :filters (mapv pr-str filters))))
      (.then #(-> % :data :searchUsers))
      (.catch (fn [e]
                (ui-errors/show-error-toast! {:text (t/t "An error occurred while searching users")})
                (throw e)))))

(defnc ChannelAdminsField
  "Props:
  - :on-change - called with all the selected users, when the dropdown closes
  - :initial-admins - the users that are selected to begin with
  - :disabled - optional"
  [{:keys [on-change initial-admins disabled]}]
  (let [debounced-search (util/use-debounce 300 search-users!)
        on-search (fn [s n]
                    (-> (debounced-search {:search-string s
                                           :results-size n})
                        (.then (fn [{:keys [nodes totalCount]}]
                                 {:items nodes
                                  :total-count totalCount}))))]
    [inputs/FormField {:label (t/t "Channel admins")}
     [combobox-multi/Combobox {:placeholder (t/t "Search by name or email")
                               :disabled disabled
                               :no-matches-text (t/t "No user found")
                               :item-title-key :fullName
                               :item-value-key :id
                               :item-subtitle-key :email
                               :item-avatar-key identity
                               :item-size :large
                               :chips-variant :user
                               :max-lines-number 5
                               :pagination-size 50
                               :initial-items* nil
                               :initial-selected-items* (or initial-admins [])
                               :on-search on-search
                               :on-save-selected (fn [{:keys [selected-items]}]
                                                   (on-change selected-items)
                                                   selected-items)
                               :close-on-select? true}]]))

(defnc DraftInfo [{:keys [editing?]}]
  [banners/InformationalBanner
   [text/Text {:variant text/Body-Md-Regular}
    (if editing?
      (t/t "The channel is created as \"draft\" and only visible to you and other admins. You can edit the description, process, submission form and evaluation later.")
      (t/t "The channel is created as \"draft\" and only visible to you and other admins. You can add a description, process, submission form and evaluation later."))]])

(defnc FormShell
  "Step 2: a centered form. Create sits below the form, and sticks to the bottom
  of the dialog when the form is taller than it. Back is in the dialog header."
  [{:keys [wide? title children on-create create-label create-disabled loading]}]
  [:div {:class "channel-create__body"}
   [:div {:class "channel-create__form"}
    [:div {:class ["channel-create__form-inner"
                   (when wide? "channel-create__form-inner--wide")]}
     title
     children]
    [:div {:class ["channel-create__pinned"
                   (when wide? "channel-create__pinned--wide")]}
     [buttons2/Button {:variant buttons2/NewPrimary
                       :tone buttons2/ToneBrand
                       :class "channel-create__create"
                       :label (or create-label (t/t "Create"))
                       :disabled create-disabled
                       :loading loading
                       :on-click on-create}]]]])

(defnc TitleField [{:keys [value on-change disabled]}]
  [inputs/FormField {:label (t/t "Title")
                     :required? true}
   [inputs/TextInput {:type "text"
                      :value value
                      :disabled disabled
                      :focus-on-mount? true
                      :on-change #(on-change (-> % .-target .-value))}]])

;; ---------------------------------------------------------------------------
;; Step 2 forms
;; ---------------------------------------------------------------------------

(defnc BlankForm []
  (let [[title set-title] (hooks/useState "")
        [space-id set-space-id] (hooks/useState nil)
        [admins set-admins] (hooks/useState [])
        [loading submit!] (use-submit
                           (fn []
                             (-> (gql-client/mutation-promise! create-channel-mutation
                                                               {:title title
                                                                :spaceId space-id})
                                 (util/delay-atleast 500)
                                 (.then (fn [result]
                                          (when-let [ideabox-id (get-in result [:data :createIdeabox :id])]
                                            (finish! ideabox-id admins)
                                            ideabox-id))))))]
    [FormShell {:on-create submit!
                :create-disabled (string/blank? title)
                :loading loading}
     [TitleField {:value title :on-change set-title :disabled loading}]
     [SpacesSelect {:space-id space-id :set-space-id set-space-id}]
     [ChannelAdminsField {:on-change set-admins}]
     [DraftInfo]]))

(defnc TemplateForm [{:keys [template]}]
  (let [{:keys [icon color]} (presentation (:id template))
        [title set-title] (hooks/useState "")
        [space-id set-space-id] (hooks/useState nil)
        [admins set-admins] (hooks/useState [])
        ;; TODO use the platform-locale instead of the active-locale
        [language set-language] (hooks/useState (format/po-locale->base-locale (t/active-locale)))
        [loading submit!] (use-submit
                           (fn []
                             (track/action "Create Channel From Template" {:id (:id template)})
                             (-> (gql-client/mutation-promise! create-from-template-mutation
                                                               {:id (:id template)
                                                                :language language
                                                                :spaceId space-id
                                                                :title title})
                                 (util/delay-atleast 500)
                                 (.then (fn [result]
                                          (when-let [ideabox-id (get-in result [:data :createIdeaboxFromTemplate :id])]
                                            (finish! ideabox-id admins)
                                            ideabox-id))))))]
    [FormShell {:title (hx/f [:div {:class "channel-create__form-title"}
                              [:div {:class "channel-create__form-title-row"}
                               [cards/IconIndicator {:icon icon :color color}]
                               [text/Text {:variant text/Heading-Md :el :h2 :class "m-0"}
                                (:title template)]]
                              [text/Text {:variant text/Body-Sm-Regular
                                          :el :p
                                          :class "channel-create__template-description m-0"}
                               (first-paragraph-text (:description template))]])
                :on-create submit!
                :create-disabled (string/blank? title)
                :loading loading}
     [TitleField {:value title :on-change set-title :disabled loading}]
     [inputs/FormField {:label (t/t "Language")}
      [select/SelectSimple {:value language
                            :disabled loading
                            :on-change (fn [{:keys [value]}]
                                         (set-language value))
                            :options [{:value (locales/locale-us-string) :label (t/t "English")}
                                      {:value (locales/locale-denmark-string) :label (t/t "Danish")}
                                      {:value (locales/locale-france-string) :label (t/t "French")}
                                      {:value (locales/locale-germany-string) :label (t/t "German")}]}]]
     [SpacesSelect {:space-id space-id :set-space-id set-space-id}]
     [ChannelAdminsField {:on-change set-admins}]
     [DraftInfo {:editing? true}]]))

(defnc DuplicateForm [{:keys [channel]}]
  (let [[title set-title] (hooks/useState (str (:title channel) " " (t/t "(Copy)")))
        [space-id set-space-id] (hooks/useState (get-in channel [:space :id]))
        ;; nil until the admins of the channel to duplicate are loaded
        [source-admins set-source-admins] (hooks/useState nil)
        [admins set-admins] (hooks/useState [])
        [copy-users set-copy-users] (hooks/useState false)
        [copy-batches set-copy-batches] (hooks/useState false)
        [loading submit!] (use-submit
                           (fn []
                             (let [options {:users copy-users
                                            ;; the admins are the ones selected here
                                            :ideaboxAdmins false
                                            :evaluationBatches copy-batches}]
                               (track/action "Copy Channel" options)
                               (-> (gql-client/mutation-promise! copy-channel-mutation
                                                                 {:id (:id channel)
                                                                  :title title
                                                                  :spaceId space-id
                                                                  :options options})
                                   (util/delay-atleast 500)
                                   (.then (fn [result]
                                            (when-let [ideabox-id (get-in result [:data :copyIdeabox :id])]
                                              (finish! ideabox-id admins)
                                              ideabox-id)))))))]
    (hooks/useEffect
     (fn []
       (-> (search-users! {:filters [{:filter/type :filter/admin-of-ideabox
                                      :filter.admin-of-ideabox/subtype :include
                                      :filter.admin-of-ideabox/ids [(:id channel)]}]})
           (.then (fn [{:keys [nodes]}]
                    (set-source-admins nodes)
                    (set-admins nodes)))
           (.catch (fn [_err]
                     (set-source-admins [])))))
     [(:id channel)])
    [FormShell {:on-create submit!
                :create-disabled (or (string/blank? title) (nil? source-admins))
                :loading loading}
     [TitleField {:value title :on-change set-title :disabled loading}]
     [SpacesSelect {:space-id space-id :set-space-id set-space-id}]
     ;; the combobox only reads its initial selection once, so remount when it's loaded
     [ChannelAdminsField {:key (if source-admins "loaded" "loading")
                          :disabled (nil? source-admins)
                          :initial-admins source-admins
                          :on-change set-admins}]
     [inputs/FormField {:label (t/t "Settings")}
      [:div {:class "d-flex flex-column"
             :style {:gap "8px"}}
       [checkbox/CheckboxWithLabel {:label (t/t "Duplicate users")
                                    :checked copy-users
                                    :on-change #(set-copy-users not)}]
       [checkbox/CheckboxWithLabel {:label (t/t "Duplicate evaluation teams")
                                    :checked copy-batches
                                    :on-change #(set-copy-batches not)}]]]
     [DraftInfo {:editing? true}]]))

(defnc ImportForm []
  (let [;; {:name .. :size .. :status (:reading :ready :failed) :content .. :error ..}
        [file set-file] (hooks/useState nil)
        ;; the File being read: a read that finishes after a removal or a new drop is ignored
        js-file-ref (hooks/useIRef nil)
        [title set-title] (hooks/useState "")
        [space-id set-space-id] (hooks/useState nil)
        [loading set-loading] (hooks/useState false)
        [error set-error] (hooks/useState nil)
        [import-result set-import-result] (hooks/useState nil) ; {:ideabox-id .. :warnings [..]}
        reset-file! (fn [file js-file]
                      (reset! js-file-ref js-file)
                      (set-file file)
                      (set-error nil)
                      (set-import-result nil))
        read! (fn [^js js-file]
                (reset-file! {:name (.-name js-file) :size (.-size js-file) :status :reading} js-file)
                (-> (util/read-file-as-text js-file)
                    (.then (fn [content]
                             (when (identical? js-file @js-file-ref)
                               (set-file assoc :status :ready :content content)
                               ;; functional update: a title typed during the read isn't clobbered
                               (set-title (fn [current]
                                            (if (string/blank? current)
                                              (or (some->> (export-title content) (str "(import) "))
                                                  current)
                                              current))))))
                    (.catch (fn [_err]
                              (when (identical? js-file @js-file-ref)
                                (set-file assoc :status :failed :error (t/t "Could not read the file")))))))
        failed! (fn [^js js-file message]
                  (reset-file! {:name (.-name js-file) :size (.-size js-file) :status :failed :error message} nil))
        on-drop (fn [accepted rejections]
                  (if-let [^js js-file (aget accepted 0)]
                    (read! js-file)
                    (some-> rejections (aget 0) .-file
                            (failed! (t/t "Only one .edn export file can be imported")))))
        on-import (fn []
                    (set-loading true)
                    (set-error nil)
                    (-> (gql-client/mutation-promise!
                         import-channel-mutation
                         {:edn (:content file)
                          :title title
                          :spaceId space-id}
                         (fn [error-response]
                           (if-let [messages (->> (gql-errors/response-errors (:response error-response) :bad-argument)
                                                  (map :message)
                                                  seq)]
                             (set-error (string/join ", " messages))
                             (ui-errors/default-mutation-error-handler error-response))
                           nil))
                        (util/delay-atleast 500)
                        (.then (fn [result]
                                 (when-let [{:keys [ideabox warnings]} (get-in result [:data :importChannel])]
                                   (if (seq warnings)
                                     (set-import-result {:ideabox-id (:id ideabox) :warnings warnings})
                                     (go-to-settings! (:id ideabox))))))
                        (.finally #(set-loading false))))]
    [FormShell {:wide? (some? file)
                :on-create (if import-result
                             #(go-to-settings! (:ideabox-id import-result))
                             on-import)
                :create-label (when import-result (t/t "Go to channel"))
                :create-disabled (and (not import-result)
                                      (or (string/blank? title) (not= :ready (:status file))))
                :loading loading}
     [inputs/FormField {:label (t/t "Upload file")
                        :description (t/t "Import a channel from an EDN export file. The channel will be created as draft")}
      (if file
        [dropzone/UploadedFile {:name (:name file)
                                :size (progress/human-readable-filesize (:size file))
                                :error (:error file)
                                :on-retry (when (and (= :failed (:status file)) @js-file-ref)
                                            #(read! @js-file-ref))
                                ;; a file changed during or after the import would get its result
                                :disabled (or loading (some? import-result))
                                :on-remove #(reset-file! nil nil)}]
        [dropzone/Dropzone {:max-files 1
                            :accept ".edn"
                            :height 226
                            :on-drop on-drop}])]
     [inputs/FormField {:label (t/t "Channel title")
                        :required? true}
      [inputs/TextInput {:type "text"
                         :value title
                         :disabled (or loading (some? import-result))
                         :on-change #(set-title (-> % .-target .-value))}]]
     [SpacesSelect {:space-id space-id :set-space-id set-space-id}]
     (when error
       [banners/CriticalBanner
        [text/Text {:variant text/Body-Md-Regular :el :p :class "mb-0"}
         error]])
     (when import-result
       [banners/WarningBanner
        [text/Text {:variant text/Body-Md-Bold :el :p}
         (t/t "Channel imported, but some settings couldn't be transferred:")]
        [:ul {:class "mb-0"}
         (for [[i w] (map-indexed vector (:warnings import-result))]
           [:li {:key i} (:message w)])]])]))

;; ---------------------------------------------------------------------------
;; Step 1
;; ---------------------------------------------------------------------------

(defn- fetch-templates* []
  (-> (gql-client/query-promise! templates-list-query {})
      (util/delay-atleast 300)
      (.then (fn [r]
               (->> (get-in r [:data :ideaboxTemplates])
                    (remove #(= blank-template-id (:id %))))))
      (ui-errors/suspending-value)))

(defnc TemplateCategoryChips* [{:keys [templates* category set-category]}]
  (let [present-categories (into #{} (keep #(:category (presentation (:id %)))) (rr/deref* templates*))]
    [:<>
     (for [{:keys [key title color]} (categories)
           :when (present-categories key)]
       [chips/ColoredChip {:key (name key)
                           :text title
                           :color color
                           :size :large
                           :chip-color :neutral
                           :selected? (= category key)
                           :on-click #(set-category key)}])]))

(defnc TemplatesGrid* [{:keys [templates* category search on-select]}]
  (let [search-term (string/lower-case (string/trim search))
        shown (->> (rr/deref* templates*)
                   (filter #(or (nil? category) (= category (:category (presentation (:id %))))))
                   (filter #(or (string/blank? search-term)
                                (string/includes? (string/lower-case (:title %)) search-term)
                                (string/includes? (string/lower-case (str (first-paragraph-text (:description %)))) search-term))))]
    (if (empty? shown)
      [text/Text {:variant text/Body-Md-Regular :el :div :class "channel-create__empty"}
       (t/t "No templates found")]
      [:div {:class "channel-create__grid"}
       (for [template shown
             :let [{:keys [icon color]} (presentation (:id template))]]
         [cards/TemplateCard {:key (:id template)
                              :variant :thumbnail
                              :title (:title template)
                              :description (first-paragraph-text (:description template))
                              :icon icon
                              :color color
                              :byline (hx/f [cards/CardByline {:label (t/t "Made by Nosco")}])
                              :on-click #(on-select template)}])])))

(defnc TemplatesTab [{:keys [on-select]}]
  ;; the Suspense boundaries must stay below the useState holding the fetch: one
  ;; above it would remount this on suspend, and refetch
  (let [[templates*] (hooks/useState fetch-templates*)
        [category set-category] (hooks/useState nil)
        [search set-search] (hooks/useState "")]
    [:<>
     [:div {:class "channel-create__filters"}
      [:div {:class "channel-create__chips"}
       [chips/Userlabel {:text (t/t "All")
                         :size :large
                         :color :neutral
                         :selected? (nil? category)
                         :on-click #(set-category nil)}]
       [React/Suspense {:fallback nil}
        [TemplateCategoryChips* {:templates* templates*
                                 :category category
                                 :set-category set-category}]]]
      [:div {:class "channel-create__search"}
       [inputs/SearchInput {:placeholder (t/t "Search")
                            :value search
                            :on-change #(set-search (-> % .-target .-value))}]]]
     [React/Suspense {:fallback (hx/f [loading/Loading])}
      [TemplatesGrid* {:templates* templates*
                       :category category
                       :search search
                       :on-select on-select}]]]))

(defn- query-channels! [{:keys [search order page-size]}]
  (gql-client/query-promise! search-channels-query
                             {:title (when-not (string/blank? search) search)
                              :stateType [:OPEN :CLOSED]
                              :orderBy {:field "CREATED_AT" :order order}
                              :pagination {:first page-size}}))

(defn- channels* [query-promise]
  (-> query-promise
      (util/delay-atleast 300)
      (.then #(get-in % [:data :searchIdeaboxes]))
      (ui-errors/suspending-value)))

(defnc ChannelsGrid* [{:keys [channels* loading on-select on-show-more]}]
  (let [{:keys [nodes pageInfo]} (rr/deref* channels*)]
    (if (empty? nodes)
      [text/Text {:variant text/Body-Md-Regular :el :div :class "channel-create__empty"}
       (t/t "No channels found")]
      [:<>
       [:div {:class "channel-create__grid"}
        (for [channel nodes
              :let [owner (some-> channel :owners :users first :user)]]
          [cards/TemplateCard {:key (:id channel)
                               :variant :image
                               :title (:title channel)
                               :description (first-paragraph-text (:description channel))
                               :image-src (:featureImage channel)
                               :byline (when owner
                                         (hx/f [cards/CardByline {:user owner
                                                                  :label (t/t "by %s" (:fullName owner))}]))
                               :on-click #(on-select channel)}])]
       (when (:hasNextPage pageInfo)
         [:div {:class "d-flex justify-content-center"}
          [buttons2/Button {:variant buttons2/NewDefault
                            :label (t/t "Show more")
                            :loading loading
                            :on-click on-show-more}]])])))

(defnc ChannelsTab [{:keys [on-select]}]
  (let [[search set-search] (hooks/useState "")
        [order set-order] (hooks/useState "DESC")
        [page-size set-page-size] (hooks/useState channels-page-size)
        ;; the Suspense boundary must stay below this useState: one above it would
        ;; remount this on suspend, and refetch
        [result* set-result*] (hooks/useState #(channels* (query-channels! {:search search
                                                                            :order order
                                                                            :page-size page-size})))
        [result* loading] (rr/useCachedSuspending result*)
        debounced-query-channels! (util/use-debounce 400 query-channels!)
        ;; debounce the query, not set-result*: loading must show from the first keystroke
        fetch! (fn [query! vars]
                 (let [vars (merge {:search search
                                    :order order
                                    :page-size channels-page-size}
                                   vars)]
                   (set-page-size (:page-size vars))
                   (set-result* (channels* (query! vars)))))]
    [:<>
     [:div {:class "channel-create__filters"}
      [:div {:class "channel-create__search"}
       [inputs/SearchInput {:placeholder (t/t "Search")
                            :loading loading
                            :value search
                            :on-change (fn [e]
                                         (let [search (-> e .-target .-value)]
                                           (set-search search)
                                           (fetch! debounced-query-channels! {:search search})))}]]
      [buttons2/Button {:variant buttons2/NewTextSubtle
                        :size buttons2/SizeMedium
                        :class "channel-create__sort"
                        :icon (if (= "DESC" order) icons2/ArrowDown-small icons2/ArrowUp-small)
                        :label (t/t "Creation date")
                        :on-click (fn []
                                    (let [order (if (= "DESC" order) "ASC" "DESC")]
                                      (set-order order)
                                      (fetch! query-channels! {:order order})))}]]
     [React/Suspense {:fallback (hx/f [loading/Loading])}
      [ChannelsGrid* {:channels* result*
                      :loading loading
                      :on-select on-select
                      :on-show-more #(fetch! query-channels! {:page-size (+ page-size channels-page-size)})}]]]))

(defnc StartStep [{:keys [on-select tab set-tab]}]
  [:div {:class "channel-create__body"}
   [:div {:class "channel-create__start"}
    [:div {:class "channel-create__actions"}
     [cards/ActionCard {:icon icons2/Add-large
                        :color :blue
                        :label (t/t "Blank channel")
                        :on-click #(on-select {:type :blank})}]
     (when (and (controllers/has-flag? :channel-import-export)
                (controllers/viewer-global-admin?))
       [cards/ActionCard {:icon icons2/Upload-large
                          :color :neutral
                          :label (t/t "Import")
                          :on-click #(on-select {:type :import})}])]
    [:div {:class "channel-create__browse"}
     [tabs2/AdminTextTabs {:class "channel-create__tabs"
                           :tabs [{:key :templates :label (t/t "Templates")}
                                  {:key :channels :label (t/t "Existing channels")}]
                           :selected-tab tab
                           :on-select set-tab}]
     ;; both stay mounted, so switching tabs doesn't refetch and flash a spinner, which
     ;; shrinks the scrolling body and makes it jump
     [:div {:class "channel-create__tab-panel"
            :hidden (not= :templates tab)}
      [TemplatesTab {:on-select #(on-select {:type :template :template %})}]]
     [:div {:class "channel-create__tab-panel"
            :hidden (not= :channels tab)}
      [ChannelsTab {:on-select #(on-select {:type :duplicate :channel %})}]]]]])

;; ---------------------------------------------------------------------------
;; The dialog
;; ---------------------------------------------------------------------------

(defn- step-title [step-type]
  (case step-type
    :start (t/t "Create new channel")
    :blank (t/t "Create blank channel")
    :import (t/t "Import channel")
    :template (t/t "Create from template")
    :duplicate (t/t "Duplicate channel")))

(defnc DialogTitle
  "The header title, with Back in front of it on step 2."
  [{:keys [title on-back]}]
  [:div {:class "channel-create__header-title"}
   (when on-back
     [:<>
      [buttons2/Button {:variant :text-mono
                        :icon icons2/ArrowLeft-small
                        :label (t/t "Back")
                        :on-click on-back}]
      [:span {:class "channel-create__header-divider"}]])
   [text/Text {:variant text/Heading-Md}
    title]])

(defnc ChannelCreateDialog [{:keys [on-close]}]
  (let [[step set-step] (hooks/useState {:type :start})
        [tab set-tab] (hooks/useState :templates)
        on-back (when-not (= :start (:type step))
                  #(set-step {:type :start}))]
    [modals3/ModalBackdrop {:showing true
                            :on-close on-close}
     [modals3/Modal {:variant :inset}
      [modals3/ModalHeader {:title (hx/f [DialogTitle {:title (step-title (:type step))
                                                       :on-back on-back}])
                            :on-close on-close}]
      [:div {:class "channel-create"}
       (case (:type step)
         :start [StartStep {:on-select set-step
                            :tab tab
                            :set-tab set-tab}]
         :blank [BlankForm]
         :import [ImportForm]
         :template [TemplateForm {:template (:template step)}]
         :duplicate [DuplicateForm {:channel (:channel step)}])]]]))

(defnc Root
  "The dialog, either over the current page after clicking a link to
  :nosco.routes/ideaboxes-copy, or on that page itself when visited directly."
  [{:keys [route]}]
  (hooks/useEffect
   (fn []
     (router/add-custom-url-handler! show-dialog-handler)
     #(router/remove-custom-url-handler! show-dialog-handler))
   [])
  (let [open? (rr/useStore dialog-store :open?)
        on-page? (= :nosco.routes/ideaboxes-copy (get-in route [:data :name]))]
    (when (or open? on-page?)
      [ChannelCreateDialog {:on-close (if open?
                                        #(hide! true)
                                        #(router/push-state :nosco.routes/ideaboxes-list))}])))
